/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Component for Edit Payment Details Button
 */
import { useCallback, useState } from 'react';
import _ from 'lodash';
import { Button, ButtonProps, Modal, App } from 'antd';
import { Form } from '@formily/antd-v5';

import SchemaField from 'lib/formily/SchemaField';
import SubmitField from 'lib/formily/SubmitField';
import useRecordPaymentForm from 'hooks/payments/useRecordPaymentForm';
import useUpdatePaymentReconciliation from 'hooks/payments/mutate/useUpdatePaymentReconciliation';
import useRunAttachmentOperations from 'hooks/mutate/useRunAttachmentOperations';
import PaymentAttachmentEditor from 'components/payments/recordPayment/PaymentAttachmentEditor';
import AppEvents from '@veritable/common/utils/appEvents';
import { getUpdatePaymentSubmitModel } from 'utils/payment/payments';
import { getPaymentAllocationPath, getPaymentsPath } from 'utils/router';
import { PAYMENT_TABS } from 'app-constants/payments';
import { NAVIGATION_PROMPT_MESSAGE } from 'components/page';
import { FHIR_ENTITY_TYPES } from '@veritable/common/constants';

type IProps = ButtonProps & {
	fhirResource: Fhir.PaymentReconciliation;
	parsedAllocationData: PatientPayment.IParsedPaymentReconciliation;
	redirectAfterSave?: boolean;
	refetchPaymentListOnSave?: boolean;
};
/**
 * Edit Payment Details Button
 */
const EditPaymentDetails: React.FC<IProps> = ({
	fhirResource,
	parsedAllocationData,
	redirectAfterSave = true,
	refetchPaymentListOnSave = true,
	...restProps
}) => {
	const { modal } = App.useApp();
	const [open, setOpen] = useState(false);
	const [pendingUploadFiles, setPendingUploadFiles] = useState<File[]>([]);
	const [pendingDeleteBlobIds, setPendingDeleteBlobIds] = useState<string[]>([]);
	const [isProcessingAttachments, setIsProcessingAttachments] = useState<boolean>(false);
	const { form, schema, initialModel } = useRecordPaymentForm(parsedAllocationData, { attachmentMode: 'none' });
	const { mutateAsync, isPending } = useUpdatePaymentReconciliation(parsedAllocationData.id);
	const [activeButton, setActiveButton] = useState<'save' | 'saveAndAllocate' | null>(null);
	const isSaving = isPending || isProcessingAttachments;
	const disableSubmit = isSaving;

	const getDeleteFileName = useCallback(
		(blobId: string) => _.find(parsedAllocationData?.files, { url: blobId })?.name || blobId,
		[parsedAllocationData?.files],
	);

	const { runAttachmentOperations } = useRunAttachmentOperations({
		entityType: FHIR_ENTITY_TYPES.paymentReconciliation,
		entityId: parsedAllocationData.id,
		getDeleteFileName,
	});

	const hasAttachmentChanges = !_.isEmpty(pendingDeleteBlobIds) || !_.isEmpty(pendingUploadFiles);

	const resetAttachmentDrafts = useCallback(() => {
		setPendingUploadFiles([]);
		setPendingDeleteBlobIds([]);
	}, []);

	const handleReset = useCallback(() => {
		form.reset();
		form.setInitialValues(initialModel);
		form.setValues(initialModel);
		resetAttachmentDrafts();
	}, [form, initialModel, resetAttachmentDrafts]);

	const handleSuccessfulSave = useCallback(
		async (isAllocate: boolean) => {
			const blobIdsToDelete = [...pendingDeleteBlobIds];
			const files = [...pendingUploadFiles];
			if (!_.isEmpty(blobIdsToDelete) || !_.isEmpty(files)) {
				setIsProcessingAttachments(true);
				try {
					await runAttachmentOperations({ files, blobIdsToDelete });
				} finally {
					setIsProcessingAttachments(false);
				}
			}
			handleReset();
			setOpen(false);

			if (!isAllocate && !redirectAfterSave) {
				return;
			}

			AppEvents.publishRedirect({
				url: isAllocate
					? getPaymentAllocationPath(parsedAllocationData.id)
					: getPaymentsPath({ tab: PAYMENT_TABS.values.unallocated }),
			});
		},
		[handleReset, pendingDeleteBlobIds, pendingUploadFiles, parsedAllocationData.id, redirectAfterSave, runAttachmentOperations],
	);

	const savePaymentDetails = useCallback(
		async (submitModel: PatientPayment.IPaymentReconciliationPatchModal, isAllocate: boolean) => {
			try {
				await mutateAsync({ modal: submitModel, refetchPaymentList: refetchPaymentListOnSave });
				await handleSuccessfulSave(isAllocate);
			} finally {
				setActiveButton(null);
			}
		},
		[handleSuccessfulSave, mutateAsync, refetchPaymentListOnSave],
	);

	const handleSubmit = async (formData: PatientPayment.IRecordPaymentFormModel, isAllocate: boolean) => {
		setActiveButton(isAllocate ? 'saveAndAllocate' : 'save');
		const submitModel = getUpdatePaymentSubmitModel({ formValues: formData, paymentFhirResource: fhirResource });

		if (!formData.amount) {
			const { destroy } = modal.confirm({
				title: 'Confirm Submission',
				okText: 'Yes, Proceed',
				content: 'Do you wish to proceed without entering the amount?',
				onOk: async () => {
					await savePaymentDetails(submitModel, isAllocate);
					destroy();
				},
				onCancel: () => setActiveButton(null),
			});
		} else {
			await savePaymentDetails(submitModel, isAllocate);
		}
	};

	const handleCancel = () => {
		if (disableSubmit) {
			return;
		}

		if (!hasAttachmentChanges && _.isEqual(form.values, initialModel)) {
			setOpen(false);
		} else {
			modal.confirm({
				closable: true,
				title: 'Discard Changes?',
				content: NAVIGATION_PROMPT_MESSAGE,
				okText: 'Yes, discard',
				onOk: () => {
					handleReset();
					setOpen(false);
				},
			});
		}
	};

	return (
		<>
			<Button type='primary' {...restProps} onClick={() => setOpen(true)}>
				{'Edit Details'}
			</Button>
			<Modal
				open={open}
				title={'Edit Payment Details'}
				closable={!disableSubmit}
				onCancel={handleCancel}
				footer={null}
				width={'50%'}
				className='tw-modal-form'
				bodyProps={{ style: { height: '50vh', minHeight: 550 } }}
				maskClosable={false}
			>
				<Form form={form} layout='vertical' key={form.id}>
					<SchemaField schema={schema} />
					{open && (
						<PaymentAttachmentEditor
							entityId={parsedAllocationData.id}
							pendingUploadFiles={pendingUploadFiles}
							onPendingUploadFilesChange={setPendingUploadFiles}
							pendingDeleteBlobIds={pendingDeleteBlobIds}
							onPendingDeleteBlobIdsChange={setPendingDeleteBlobIds}
							disabled={disableSubmit}
						/>
					)}
					<div className='tw-modal-form-footer'>
						<Button key='reset' onClick={handleReset} disabled={disableSubmit}>
							{'Reset'}
						</Button>
						<SubmitField
							key={`${form.id}-save`}
							disabled={disableSubmit}
							loading={isSaving && activeButton === 'save'}
							title='Save'
							initialModel={initialModel}
							onSubmit={(formData) => handleSubmit(formData, false)}
						/>
						<SubmitField
							key={`${form.id}-allocate`}
							disabled={disableSubmit}
							loading={isSaving && activeButton === 'saveAndAllocate'}
							title='Save & Allocate'
							initialModel={initialModel}
							onSubmit={(formData) => handleSubmit(formData, true)}
						/>
					</div>
				</Form>
			</Modal>
		</>
	);
};

export default EditPaymentDetails;
